import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, CalendarCheck } from 'lucide-react';
import { Button } from './ui/button';
import { JANE_BOOKING_URL } from '../data/janeServices';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_266cf7bd-85bf-4fa7-83fe-d09732430dba/artifacts/urov4aah_Screenshot%202026-02-16%20at%209.23.56%E2%80%AFPM.png";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      data-testid="header"
      className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-md border-b border-primary/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <img 
              src={LOGO_URL} 
              alt="Bayside Birth & Pelvic Health" 
              className="h-14 w-auto"
            />
            <div className="hidden sm:block">
              <h1 className="text-lg font-medium text-foreground leading-tight">
                Bayside Birth
              </h1>
              <p className="text-xs text-muted-foreground">& Pelvic Health</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  isActive(link.path) 
                    ? 'text-primary' 
                    : 'text-muted-foreground'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href={JANE_BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-book-online-button"
            >
              <Button 
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6"
              >
                <CalendarCheck className="w-4 h-4 mr-2" />
                Book Online
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden p-2 text-foreground"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav 
            data-testid="mobile-nav"
            className="md:hidden py-4 border-t border-primary/10"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  className={`text-base font-medium py-2 transition-colors ${
                    isActive(link.path) 
                      ? 'text-primary' 
                      : 'text-muted-foreground'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <a
                href={JANE_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMenuOpen(false)}
                data-testid="mobile-book-online-button"
              >
                <Button 
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full mt-2"
                >
                  <CalendarCheck className="w-4 h-4 mr-2" />
                  Book Online
                </Button>
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
